import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import SEO from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Achievements() {
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAchievements();
  }, []);

  const fetchAchievements = async () => {
    try {
      const response = await axios.get(`${API}/achievements`);
      setAchievements(response.data);
    } catch (error) {
      console.error("Error fetching achievements:", error);
    } finally {
      setLoading(false);
    }
  };

  // Format date for display
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long'
    });
  };

  // Format year
  const formatYear = (dateStr) => {
    return new Date(dateStr).getFullYear();
  };

  // Group achievements by year
  const achievementsByYear = achievements.reduce((acc, achievement) => {
    const year = new Date(achievement.date).getFullYear();
    if (!acc[year]) acc[year] = [];
    acc[year].push(achievement);
    return acc;
  }, {});

  const years = Object.keys(achievementsByYear).sort((a, b) => b - a);

  return (
    <>
      <SEO 
        title="Our Story | Filmcable"
        description="The Filmcable journey - from founding to becoming a trusted voice in film journalism."
      />
      
      <div className="min-h-screen bg-white" data-testid="achievements-page">
        {/* Hero Section - Minimal editorial style */}
        <section className="border-b border-zinc-100">
          <div className="container py-20 md:py-32">
            <div className="max-w-2xl">
              <p className="font-accent text-xs font-bold uppercase tracking-[0.3em] text-[#FF66C4] mb-6">
                Est. 2024
              </p>
              <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-bold text-zinc-900 leading-[0.9] mb-8">
                Our Story
              </h1>
              <p className="text-lg text-zinc-500 leading-relaxed max-w-lg">
                A chronicle of milestones in our mission to deliver 
                authoritative coverage of cinema and the global film industry.
              </p>
            </div>
          </div>
        </section>

        {/* Timeline Section - Clean editorial design */}
        <section className="py-20 md:py-32">
          <div className="container">
            {loading ? (
              <div className="flex justify-center py-20">
                <div className="w-6 h-6 border-2 border-[#FF66C4] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : achievements.length === 0 ? (
              <div className="text-center py-20 max-w-md mx-auto">
                <p className="font-serif text-2xl text-zinc-300 mb-4">
                  Coming Soon
                </p>
                <p className="text-zinc-400">
                  Our story is being written.
                </p>
              </div>
            ) : (
              <div className="max-w-3xl mx-auto">
                {years.map((year, yearIndex) => (
                  <div key={year} className="mb-20 last:mb-0">
                    {/* Year Header */}
                    <div className="flex items-center gap-6 mb-12">
                      <span className="font-serif text-8xl md:text-9xl font-bold text-zinc-100 leading-none select-none">
                        {year}
                      </span>
                    </div>
                    
                    {/* Achievements for this year */}
                    <div className="space-y-0 border-l-2 border-zinc-100 ml-4">
                      {achievementsByYear[year].map((achievement, index) => (
                        <article 
                          key={achievement.id}
                          className="relative pl-8 pb-12 last:pb-0 group"
                          data-testid={`achievement-${achievement.id}`}
                        >
                          {/* Timeline dot */}
                          <div className="absolute left-0 top-1 w-2 h-2 -translate-x-[5px] bg-zinc-200 group-hover:bg-[#FF66C4] transition-colors duration-300" />
                          
                          {/* Date */}
                          <time className="font-accent text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400 block mb-3">
                            {formatDate(achievement.date)}
                          </time>
                          
                          {/* Title */}
                          <h3 className="font-serif text-2xl md:text-3xl font-bold text-zinc-900 mb-3 leading-tight group-hover:text-[#FF66C4] transition-colors duration-300">
                            {achievement.title}
                          </h3>
                          
                          {/* Description */}
                          <p className="text-zinc-500 leading-relaxed max-w-xl">
                            {achievement.description}
                          </p>
                          
                          {/* Link */}
                          {achievement.link && (
                            <a 
                              href={achievement.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-block mt-4 font-accent text-xs font-bold uppercase tracking-wider text-[#FF66C4] hover:underline"
                            >
                              Read More
                            </a>
                          )}
                        </article>
                      ))}
                    </div>
                  </div>
                ))}
                
                {/* End marker */}
                <div className="text-center pt-12 border-t border-zinc-100">
                  <p className="font-accent text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-300">
                    The story continues
                  </p>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* CTA Section - Minimal */}
        <section className="border-t border-zinc-100 bg-zinc-50/50">
          <div className="container py-20 text-center">
            <p className="font-serif text-xl text-zinc-400 mb-8">
              Follow our journey
            </p>
            <div className="flex justify-center gap-4">
              <Link 
                to="/articles"
                className="px-8 py-4 bg-zinc-900 text-white hover:bg-[#FF66C4] font-accent text-xs font-bold uppercase tracking-wider transition-colors"
              >
                Read Editorial
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
